/**
 * Mirrors packages/amgroulette_v2/lib/domain/entities/bet_collection.dart
 * — keyed by numeric betID (roulette has 162 distinct bet areas).
 */
export interface BetEntry {
  betId: number;
  amount: number;
  chips: number[]; // history of chip denominations placed
}

export class BetCollection {
  private entries: Map<number, BetEntry> = new Map();

  static empty(): BetCollection {
    return new BetCollection();
  }

  copy(): BetCollection {
    const c = new BetCollection();
    for (const [k, v] of this.entries) {
      c.entries.set(k, { betId: v.betId, amount: v.amount, chips: [...v.chips] });
    }
    return c;
  }

  add(betId: number, amount: number): void {
    const cur = this.entries.get(betId);
    if (cur) {
      cur.amount += amount;
      cur.chips.push(amount);
    } else {
      this.entries.set(betId, { betId, amount, chips: [amount] });
    }
  }

  clear(): void {
    this.entries.clear();
  }

  clearBet(betId: number): void {
    this.entries.delete(betId);
  }

  amountFor(betId: number): number {
    return this.entries.get(betId)?.amount ?? 0;
  }

  chipsFor(betId: number): readonly number[] {
    return this.entries.get(betId)?.chips ?? [];
  }

  get total(): number {
    let n = 0;
    for (const v of this.entries.values()) n += v.amount;
    return n;
  }

  get isEmpty(): boolean {
    return this.entries.size === 0;
  }

  get betIds(): readonly number[] {
    return Array.from(this.entries.keys());
  }

  entriesList(): readonly BetEntry[] {
    return Array.from(this.entries.values());
  }

  /**
   * Convert to /action/bet body lines. Field name is `amount`.
   */
  toBetLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      if (v.amount <= 0) continue;
      out.push({ betItemId: v.betId, amount: v.amount });
    }
    return out;
  }

  /**
   * Like toBetLines() but emits ONE line per INDIVIDUAL chip placed on a bet
   * area, rather than a single line carrying the area's accumulated total.
   *
   * Used for multiplayer hub submission: the SignalR hub validates each
   * PlaceBet `amount` against the configured chip denominations and rejects a
   * summed total (e.g. 100+100+100 -> 300) with "Invalid Chip Amount". Sending
   * one PlaceBet per chip keeps every amount a valid denomination, matching
   * the original per-click submission behaviour.
   *
   * Mirrors toBetLines()'s betItemId derivation (v.betId) and skip condition
   * exactly; for each betItemId the sum of toChipLines amounts equals the
   * toBetLines amount.
   */
  toChipLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      if (v.amount <= 0) continue;
      for (const chip of v.chips) {
        if (chip <= 0) continue;
        out.push({ betItemId: v.betId, amount: chip });
      }
    }
    return out;
  }
}
